import { CartIcon } from '../components/Icons';
import { useState, useEffect } from 'react';
import { fetchSettings } from '../db';

export default function CartPage({ cartItems, onNavigate, onUpdateQuantity, onRemoveItem }) {
  const [shippingConfig, setShippingConfig] = useState(null);

  useEffect(() => {
    fetchSettings().then(settings => {
      setShippingConfig({
        enabled: settings.shipping_enabled !== 'false'
      });
    }).catch(() => {});
  }, []);

  const groupedCart = cartItems?.reduce((acc, item) => {
    const key = `${item.id}${item.size ? '-' + item.size : ''}${item.color ? '-' + item.color : ''}`;
    if (!acc[key]) acc[key] = { ...item, quantity: 0 };
    acc[key].quantity += 1;
    return acc;
  }, {});
  const uniqueItems = Object.values(groupedCart || {});

  const total = uniqueItems.reduce((sum, item) => {
    const price = typeof item.price === 'number'
      ? item.price
      : (parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0);
    return sum + (price * item.quantity);
  }, 0);

  return (
    <div className="min-h-screen bg-[#FAF3F0] pb-24 lg:pb-12">
      <div className="max-w-5xl w-full mx-auto px-4 lg:px-8 pt-6">
        <h1 className="text-xl font-black text-[#1A1A2E] mb-5">Shopping Cart</h1>

        {!cartItems || cartItems.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 w-full flex flex-col items-center text-center shadow-sm border border-gray-100">
            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
              <CartIcon className="w-8 h-8 text-[#F47B20]" />
            </div>
            <p className="text-[#1A1A2E] text-lg font-black mb-2">Your cart is empty</p>
            <p className="text-gray-500 text-sm max-w-xs mb-6 leading-relaxed">Explore our premium bridal collection and add something beautiful!</p>
            <button onClick={() => onNavigate('shop')} className="px-6 py-3 bg-[#1A1A2E] hover:bg-black transition-colors text-white rounded-xl font-bold text-sm">Start Shopping</button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5 items-start">

            {/* Left: Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-4">
                  <h2 className="text-base font-bold text-[#1A1A2E]">Cart Items</h2>
                  <span className="text-xs text-gray-400 font-medium">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</span>
                </div>

                <div className="space-y-3">
                  {uniqueItems.map((item, idx) => {
                    const price = typeof item.price === 'number'
                      ? item.price
                      : (parseFloat(String(item.price).replace(/[^\d.]/g, '')) || 0);
                    const itemImg = Array.isArray(item.images)
                      ? item.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                      : (!item.image?.match(/\.(mp4|webm|ogg)$/i) ? item.image : null);

                    return (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors">
                        {/* Thumbnail */}
                        <div
                          onClick={() => onNavigate('productdetail', { product: item })}
                          className="w-14 h-14 bg-[#FAF3F0] rounded-xl overflow-hidden shrink-0 cursor-pointer"
                        >
                          {itemImg
                            ? <img src={itemImg} alt={item.name} className="w-full h-full object-cover" />
                            : <span className="w-full h-full flex items-center justify-center text-2xl">{item.emoji || '🛍️'}</span>
                          }
                        </div>

                        {/* Info: name + size + price */}
                        <div className="flex-1 min-w-0">
                          <p
                            onClick={() => onNavigate('productdetail', { product: item })}
                            className="text-sm font-bold text-[#1A1A2E] leading-tight cursor-pointer hover:text-[#F47B20] transition-colors line-clamp-1"
                          >
                            {item.name}
                          </p>
                          <div className="flex gap-1 flex-wrap mt-0.5">
                            {item.size && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded inline-block">Size: {item.size}</span>}
                            {item.color && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded inline-block">Color: {item.color}</span>}
                          </div>
                          <p className="text-sm font-bold text-[#F47B20] mt-1">₦{(price * item.quantity).toLocaleString()}</p>
                        </div>

                        {/* Qty controls */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 shrink-0">
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity - 1, item.size, item.color)}
                            className="w-6 h-6 flex items-center justify-center rounded-md bg-white text-gray-600 hover:text-[#F47B20] text-sm font-bold transition-colors shadow-sm"
                          >−</button>
                          <span className="w-5 text-center font-bold text-[#1A1A2E] text-xs">{item.quantity}</span>
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity + 1, item.size, item.color)}
                            disabled={item.stock !== undefined && item.quantity >= item.stock}
                            className={`w-6 h-6 flex items-center justify-center rounded-md bg-white text-sm font-bold transition-colors shadow-sm ${item.stock !== undefined && item.quantity >= item.stock ? 'opacity-40 cursor-not-allowed text-gray-400' : 'text-gray-600 hover:text-[#F47B20]'}`}
                          >+</button>
                        </div>

                        {/* Delete */}
                        <button
                          onClick={() => onRemoveItem(item.id, item.size, item.color)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right: Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 sticky top-24">
                <h2 className="text-base font-bold text-[#1A1A2E] mb-4 border-b border-gray-100 pb-3">Order Summary</h2>

                {/* Promo */}
                <div className="mb-4">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Discount Code</label>
                  <div className="flex gap-1.5">
                    <input type="text" placeholder="Enter code" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#F47B20] transition-colors" />
                    <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-3 py-2 rounded-lg text-xs transition-colors">Apply</button>
                  </div>
                </div>

                <div className="space-y-2 mb-4 pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-bold text-[#1A1A2E]">₦{total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Shipping</span>
                    <span className="text-[10px] font-bold text-green-600">
                      {(shippingConfig && !shippingConfig.enabled) || (cartItems && cartItems.length > 0 && cartItems.every(i => i.is_free_shipping))
                        ? 'Free Shipping' 
                        : 'Calculated at checkout'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-4 pt-3 border-t border-gray-100">
                  <span className="font-black text-base text-[#1A1A2E]">Total</span>
                  <span className="font-black text-xl text-[#F47B20]">₦{total.toLocaleString()}</span>
                </div>

                <button
                  onClick={() => onNavigate('checkout')}
                  className="w-full bg-[#1A1A2E] hover:bg-black text-white font-bold py-3 px-4 rounded-xl text-sm transition-colors shadow-md flex items-center justify-center gap-2"
                >
                  Checkout Now
                </button>

                <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-gray-400">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  Secure SSL Checkout
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
